import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { usersAPI, groupsAPI, photosAPI, UserFilters } from '../services/api';
import { User } from '../types';
import LoadingSpinner from './LoadingSpinner';
import Header from './Header';
import AvatarModal from './AvatarModal';
import UserDetailsCard from './UserDetailsCard';
import './Users.css';

const Users: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [groups, setGroups] = useState<string[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    pages: 0
  });
  const [filters, setFilters] = useState<UserFilters>({});
  const [showFilters, setShowFilters] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAvatar, setSelectedAvatar] = useState<{
    userId: string | number;
    userName?: string;
    avatarUrls: string[];
  } | null>(null);
  const loadUsersRef = useRef<(page?: number, isFilterUpdate?: boolean) => void>(() => {});

  const loadUsers = useCallback(async (page: number = 1, isFilterUpdate: boolean = false) => {
    try {
      if (isFilterUpdate) {
        setFilterLoading(true);
        setIsTransitioning(true);
      } else {
        setLoading(true);
      }
      setError(null);
      
      const response = await usersAPI.getUsers(page, pagination.limit, { ...filters, search: searchTerm });
      
      // Плавное обновление списка пользователей
      if (isFilterUpdate) {
        setTimeout(() => {
          setUsers(response.users);
          setPagination(response.pagination);
          setIsTransitioning(false);
        }, 150);
      } else {
        setUsers(response.users);
        setPagination(response.pagination);
      }
    } catch (err: any) {
      setError(err.message || 'Ошибка при загрузке пользователей');
      setIsTransitioning(false);
    } finally {
      setLoading(false);
      setFilterLoading(false);
    }
  }, [pagination.limit, filters, searchTerm]);

  loadUsersRef.current = loadUsers;

  const loadGroups = useCallback(async () => {
    try {
      const response = await groupsAPI.getGroups();
      setGroups(response.groups || []);
    } catch (err: any) {
      console.error('Ошибка при загрузке групп:', err);
      setGroups([]);
    }
  }, []);

  // Обработка chatId из URL и загрузка групп (без зависимости от loadUsers, чтобы не перезапускаться при каждом вводе в поиск)
  useEffect(() => {
    const chatIdFromUrl = searchParams.get('chatId');
    if (chatIdFromUrl) {
      setSearchTerm(chatIdFromUrl);
      setSearchParams({});
    }
    loadGroups();
  }, [searchParams, setSearchParams]);

  // Первичная загрузка пользователей только при монтировании
  useEffect(() => {
    loadUsersRef.current(1, false);
  }, []);

  // Дебаунс для поиска и фильтров — запрос только через 300 мс после последнего изменения
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadUsers(1, true);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [filters, searchTerm, loadUsers]);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= pagination.pages) {
      loadUsers(newPage);
    }
  };

  const handleFilterChange = (filterType: keyof UserFilters, value: any) => {
    setFilters(prev => {
      const newFilters = {
        ...prev,
        [filterType]: value
      };
      
      // Удаляем пустые фильтры
      Object.keys(newFilters).forEach(key => {
        const filterValue = newFilters[key as keyof UserFilters];
        if (filterValue === undefined || filterValue === null || 
            (Array.isArray(filterValue) && filterValue.length === 0)) {
          delete newFilters[key as keyof UserFilters];
        }
      });
      
      return newFilters;
    });
  };

  const clearFilters = () => {
    setFilters({});
    setSearchTerm('');
  };

  const getActiveFiltersCount = () => {
    const filterCount = Object.values(filters).filter(value => {
      if (Array.isArray(value)) return value.length > 0;
      return value !== undefined && value !== null;
    }).length;
    return filterCount + (searchTerm.trim().length > 0 ? 1 : 0);
  };

  const handleAvatarClick = async (userId: string | number, userName?: string) => {
    try {
      // Загружаем аватарки для модального окна
      const numericUserId = typeof userId === 'string' ? parseInt(userId, 10) : userId;
      const response = await photosAPI.getUserPhotos(numericUserId);
      
      let avatarUrls: string[] = [];
      if (response.success && response.photos && response.photos.length > 0) {
        const baseURL = process.env.REACT_APP_API_URL || 'https://api.denisphones.org';
        // Берем последние 3 фотографии
        const recentPhotos = response.photos.slice(0, 3);
        avatarUrls = recentPhotos.map(photo => `${baseURL}${photo.url}`);
      }
      
      setSelectedAvatar({
        userId,
        userName,
        avatarUrls
      });
    } catch (error) {
      console.error('Ошибка загрузки аватаров для модального окна:', error);
      setSelectedAvatar({
        userId,
        userName,
        avatarUrls: []
      });
    }
  };

  const closeAvatarModal = () => {
    setSelectedAvatar(null);
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="users-error">
        <h2>Ошибка загрузки пользователей</h2>
        <p>{error}</p>
        <button onClick={() => loadUsers()} className="retry-button">
          Попробовать снова
        </button>
      </div>
    );
  }

  return (
    <div className="users-page">
      <Header title="Пользователи" />
      <div className="users-container">
        <div className="users-header">
          <div className="users-stats">
            Всего: {pagination.total} | Страница {pagination.page} из {pagination.pages}
            {filterLoading && <span className="filter-loading-indicator">🔄</span>}
          </div>
          <div className="users-controls">
            <div className="search-container">
              <input
                type="text"
                placeholder="Поиск по имени..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
              {searchTerm && (
                <button 
                  className="clear-search"
                  onClick={() => setSearchTerm('')}
                  title="Очистить поиск"
                >
                  ×
                </button>
              )}
            </div>
            <button 
              className={`filter-toggle ${showFilters ? 'active' : ''}`}
              onClick={() => setShowFilters(!showFilters)}
            >
              🔍 Фильтры {getActiveFiltersCount() > 0 && `(${getActiveFiltersCount()})`}
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="filters-panel">
            <div className="filters-grid">
              {/* Фильтр по группам */}
              <div className="filter-group">
                <label className="filter-label">Группы:</label>
                <div className="filter-checkboxes">
                  <label className="filter-checkbox">
                    <input
                      type="checkbox"
                      checked={!filters.groups || filters.groups.length === 0}
                      onChange={(e) => {
                        if (e.target.checked) {
                          handleFilterChange('groups', []);
                        }
                      }}
                    />
                    Все группы
                  </label>
                  {groups && groups.map(group => (
                    <label key={group} className="filter-checkbox">
                      <input
                        type="checkbox"
                        checked={filters.groups?.includes(group) || false}
                        onChange={(e) => {
                          const currentGroups = filters.groups || [];
                          if (e.target.checked) {
                            handleFilterChange('groups', [...currentGroups, group]);
                          } else {
                            handleFilterChange('groups', currentGroups.filter(g => g !== group));
                          }
                        }}
                      />
                      {group}
                    </label>
                  ))}
                </div>
              </div>

              {/* Фильтр по статусам */}
              <div className="filter-group">
                <label className="filter-label">Статусы:</label>
                <div className="filter-checkboxes">
                  <label className="filter-checkbox">
                    <input
                      type="checkbox"
                      checked={filters.active === true}
                      onChange={(e) => handleFilterChange('active', e.target.checked ? true : undefined)}
                    />
                    Активные
                  </label>
                  <label className="filter-checkbox">
                    <input
                      type="checkbox"
                      checked={filters.blocked === true}
                      onChange={(e) => handleFilterChange('blocked', e.target.checked ? true : undefined)}
                    />
                    Заблокированные
                  </label>
                  <label className="filter-checkbox">
                    <input
                      type="checkbox"
                      checked={filters.graduated === true}
                      onChange={(e) => handleFilterChange('graduated', e.target.checked ? true : undefined)}
                    />
                    Выпускники
                  </label>
                </div>
              </div>

              {/* Фильтр по типам пользователей */}
              <div className="filter-group">
                <label className="filter-label">Типы пользователей:</label>
                <div className="filter-checkboxes">
                  <label className="filter-checkbox">
                    <input
                      type="checkbox"
                      checked={filters.teachers === true}
                      onChange={(e) => handleFilterChange('teachers', e.target.checked ? true : undefined)}
                    />
                    Учителя
                  </label>
                  <label className="filter-checkbox">
                    <input
                      type="checkbox"
                      checked={filters.newUsers === true}
                      onChange={(e) => handleFilterChange('newUsers', e.target.checked ? true : undefined)}
                    />
                    Новые пользователи
                  </label>
                </div>
              </div>

              {/* Фильтр по приложениям */}
              <div className="filter-group">
                <label className="filter-label">Приложения:</label>
                <div className="filter-checkboxes">
                  <label className="filter-checkbox">
                    <input
                      type="checkbox"
                      checked={filters.hasApp === true}
                      onChange={(e) => handleFilterChange('hasApp', e.target.checked ? true : undefined)}
                    />
                    С мобильным приложением
                  </label>
                  <label className="filter-checkbox">
                    <input
                      type="checkbox"
                      checked={filters.hasAppToken === true}
                      onChange={(e) => handleFilterChange('hasAppToken', e.target.checked ? true : undefined)}
                    />
                    С App Token
                  </label>
                </div>
              </div>
            </div>

            <div className="filters-actions">
              <button className="clear-filters-btn" onClick={clearFilters}>
                Очистить фильтры
              </button>
            </div>
          </div>
        )}

      <div className={`users-grid ${isTransitioning ? 'transitioning' : ''}`}>
        {users.map((user) => (
          <div key={user._id} className="user-card">
            <UserDetailsCard
              user={user}
              onAvatarClick={handleAvatarClick}
              onOpenChat={(chatId) => navigate(`/chats?chatId=${chatId}`)}
            />
          </div>
        ))}
      </div>

      {pagination.pages > 1 && (
        <div className="pagination">
          <button 
            onClick={() => handlePageChange(pagination.page - 1)}
            disabled={pagination.page <= 1}
            className="pagination-button"
          >
            ← Предыдущая
          </button>
          
          <span className="pagination-info">
            {pagination.page} из {pagination.pages}
          </span>
          
          <button 
            onClick={() => handlePageChange(pagination.page + 1)}
            disabled={pagination.page >= pagination.pages}
            className="pagination-button"
          >
            Следующая →
          </button>
        </div>
      )}

      {users.length === 0 && !loading && (
        <div className="no-users">
          <h3>Пользователи не найдены</h3>
          <p>В системе пока нет пользователей</p>
        </div>
      )}
      </div>

      {/* Модальное окно для увеличенного просмотра аватарок */}
      <AvatarModal
        isOpen={selectedAvatar !== null}
        onClose={closeAvatarModal}
        avatarUrls={selectedAvatar?.avatarUrls || []}
        userName={selectedAvatar?.userName}
        userId={selectedAvatar?.userId || ''}
      />
    </div>
  );
};

export default Users;